import { type GetHookResponse } from '../hooks'
import { gameKey } from '../keys'
import { useTantalimQuery } from '../query'
import { useUserCache } from '../user'
import { type GameCache, toNewGameData, useGameCache } from './cache'
import {
  type DecoratedGameUser,
  type GameGetInput,
  type GameGetSuccess,
  type GameId,
  HOUR,
  type TinyUser,
} from '@terros/common'

export function useGame(input: GameGetInput): GetHookResponse<GameGetSuccess> {
  const cache = useGameCache()
  const userCache = useUserCache()

  const writeCache = (data: GameGetSuccess): void => {
    const { participants } = data
    for (const participant of participants) {
      if (participant.type === 'group') {
        for (const user of participant.users) {
          userCache.setTinyUser(toTinyUser(user))
        }
        continue
      }
      userCache.setTinyUser(toTinyUser(participant))
    }
  }

  return useTantalimQuery<GameGetInput, GameGetSuccess, GameGetSuccess>({
    url: '/game/get',
    queryKey: gameKey(input.gameId),
    input,
    convertResponse: (data) => {
      writeCache(data)
      return { ...data, game: toNewGameData(data.game) }
    },
    options: {
      gcTime: 6 * HOUR,
      staleTime: HOUR,
      placeholderData: buildPlaceholder(cache, input.gameId),
    },
  })
}

const buildPlaceholder = (cache: GameCache, gameId?: GameId): GameGetSuccess | undefined => {
  const cachedGameList = cache.getListItem(gameId)
  if (cachedGameList) {
    return {
      game: cachedGameList,
      type: 'success',
      participants: [],
    }
  }
}

const toTinyUser = (participant: DecoratedGameUser): TinyUser => {
  return {
    userId: participant.participantId,
    firstName: participant.firstName,
    lastName: participant.lastName,
    avatarBlurhash: participant.avatarBlurhash,
    avatarUrl: participant.avatarUrl,
    preferredName: participant.preferredName,
    lastAccess: participant.lastAccess,
    clientUserId: participant.clientUserId,
  }
}
